import {useEffect} from "react";
import {AlertTriangle, Check, Info, Loader2, X} from "lucide-react";
import {Toaster} from "sonner";
import {Portal} from "./Portal";
import {installToastErrorHaptics} from "../../../utils/toastHaptics";

/**
 * Единственное всплывающее уведомление приложения.
 *
 * Все вызовы `toast.*` из любой точки приложения рисуются этим шаблоном:
 * тёмная стеклянная плашка с монохромной иконкой, текстом и крестиком.
 * Статус (успех / ошибка / предупреждение) различается **иконкой, а не цветом
 * подложки** — цветные зелёные и красные плашки спорили с тёплым фоном
 * приложения и резали глаз. Материал — то же тёмное стекло, что у нижней
 * навигации, поэтому уведомление читается и на плоском фоне, и поверх
 * фотографий анкет.
 *
 * Вид задаётся здесь и в `styles/app.css` (секция «Уведомления»), а не на
 * местах вызова: ни один `toast.success(...)` в приложении не передаёт свои
 * стили, иконки или цвета.
 *
 * Положение: `--rla-toast-top` учитывает `env(safe-area-inset-top)`. Раньше
 * стоял «голый» `offset="16px"`, из-за чего на iOS плашка вылезала в полосу
 * системного/браузерного интерфейса, а вынесенный наружу крестик Sonner
 * (`translate(-35%, -35%)`) уходил под адресную строку. Крестик переехал
 * внутрь плашки — см. `.rla-toast-close`.
 *
 * `Portal` здесь обязателен, и это главное исправление «уведомления всплывают
 * сзади». `#root` в `styles/index.css` имеет `position: fixed` — а фиксированный
 * элемент создаёт **контекст наложения** независимо от z-index. Тостер жил
 * внутри `#root`, поэтому его z-index (сколько ни ставь — было 99999)
 * сравнивался только с соседями внутри `#root`, а сам `#root` при `z-index:
 * auto` лежал на нулевом уровне body. Модалки же портируются в body с
 * `z-[80]`…`z-[200]` — то есть весь `#root` вместе с уведомлением уходил под
 * любую открытую модалку и её затенение. Портал выносит тостер на уровень
 * body, и 999999999 наконец означает то, что написано.
 */
export function AppToaster() {
    // Тактильный отклик на ошибки подключаем здесь же, где рисуются все тосты:
    // одно место отвечает и за вид, и за ощущение уведомления (см. toastHaptics).
    useEffect(() => {
        installToastErrorHaptics();
    }, []);

    return (
        <Portal>
            <Toaster
                position="top-center"
                /* Одно окно за раз: «стопка» из тостов подглядывала из-под верхнего
                   и читалась как артефакт вёрстки. */
                visibleToasts={1}
                duration={3600}
                closeButton
                offset={{top: "var(--rla-toast-top)", bottom: "calc(24px + env(safe-area-inset-bottom))", left: "16px", right: "16px"}}
                mobileOffset={{top: "var(--rla-toast-top)", bottom: "calc(24px + env(safe-area-inset-bottom))", left: "16px", right: "16px"}}
                icons={{
                    success: <Check size={18} strokeWidth={2.5} aria-hidden/>,
                    error: <AlertTriangle size={18} strokeWidth={2.25} aria-hidden/>,
                    warning: <AlertTriangle size={18} strokeWidth={2.25} aria-hidden/>,
                    info: <Info size={18} strokeWidth={2.25} aria-hidden/>,
                    loading: <Loader2 size={18} strokeWidth={2.25} className="rla-toast-spin" aria-hidden/>,
                    close: <X size={15} strokeWidth={2.5} aria-hidden/>,
                }}
                toastOptions={{
                    /* Свои стили вместо стилей Sonner: иначе каждое правило пришлось
                       бы помечать !important, перебивая инжектируемый stylesheet. */
                    unstyled: true,
                    classNames: {
                        toast: "rla-toast",
                        title: "rla-toast-title",
                        description: "rla-toast-desc",
                        icon: "rla-toast-icon",
                        content: "rla-toast-content",
                        closeButton: "rla-toast-close",
                        actionButton: "rla-toast-action",
                        cancelButton: "rla-toast-cancel",
                    },
                }}
            />
        </Portal>
    );
}
